import React, { useState } from "react";
import { FaSearch } from "react-icons/fa";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "/api";

function getAuthValue(key) {
  return localStorage.getItem(key) ?? sessionStorage.getItem(key);
}

function safeParse(str) {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

function pickText(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
}

function normalizeRolesUsuarioFromSso(user, sourceList) {
  if (Array.isArray(user?.rolesUsuario) && user.rolesUsuario.length > 0) {
    return user.rolesUsuario;
  }

  const source = Array.isArray(sourceList) && sourceList.length > 0 ? sourceList : [user];

  const roles = source
    .filter((item) => item && typeof item === "object")
    .map((item) => {
      const tipoRol = pickText(item.tipoRol, item.tipo_usuario, item.cargo, "USER");
      const detalle = {
        idCarrera: pickText(item.idCarrera, item.id_carrera),
        nombreCarrera: pickText(item.nombreCarrera, item.carrera),
        ultimoSemestreActivo: pickText(item.ultimoSemestreActivo, item.ultimo_semestre_activo),
        unidadAcademica: pickText(item.unidadAcademica, item.unidad_academica),
        unidadOrganizativa: pickText(item.unidadOrganizativa, item.departamento),
        sede: pickText(item.sede),
        modalidad: pickText(item.modalidad),
        curso: pickText(item.curso),
        paralelo: pickText(item.paralelo),
        nombreRol: pickText(item.nombreRol, item.cargo, item.tipo_usuario, tipoRol),
      };
      return { tipoRol, detallesRol: [detalle] };
    });

  if (roles.length > 0) return roles;
  return [{ tipoRol: "USER", detallesRol: [] }];
}

function normalizeSsoUserForUsage(user, sourceList) {
  if (!user || typeof user !== "object") return null;

  const identificacion = pickText(user.identificacion, user.cedula);
  if (!identificacion) return null;

  return {
    codigoErp: pickText(user.codigoErp, user.codigo_erp),
    tipoIdentificacion: pickText(user.tipoIdentificacion, user.tipo_identificacion, "CEDULA"),
    identificacion,
    nombres: pickText(user.nombres),
    apellidos: pickText(user.apellidos),
    numeroCelular: pickText(user.numeroCelular, user.numero_celular),
    emailInstitucional: pickText(user.emailInstitucional, user.correo_institucional, user.email),
    emailPersonal: pickText(user.emailPersonal, user.correo_personal),
    sexo: pickText(user.sexo),
    rolesUsuario: normalizeRolesUsuarioFromSso(user, sourceList),
  };
}

function buildAnonUser() {
  return {
    codigoErp: "99999999999",
    tipoIdentificacion: "CEDULA",
    identificacion: "99999999999",
    nombres: "ANONIMO",
    apellidos: "(Acceso público)",
    numeroCelular: "",
    emailInstitucional: "anonimo@ucacue.edu.ec",
    emailPersonal: "",
    sexo: "N/A",
    rolesUsuario: [
      {
        tipoRol: "ANONIMO",
        detallesRol: [
          {
            nombreCarrera: "Acceso Público",
            unidadAcademica: "Visitante",
            sede: "Externo",
            modalidad: "Web",
            curso: "",
            paralelo: "",
            nombreRol: "ANONIMO",
            unidadOrganizativa: "Visitante",
          },
        ],
      },
    ],
  };
}

function buildGuestUser(invCode) {
  return {
    codigoErp: "99999999999",
    tipoIdentificacion: "CEDULA",
    identificacion: invCode || "99999999999",
    nombres: "INVITADO",
    apellidos: "(Acceso público)",
    numeroCelular: "",
    emailInstitucional: "invitado@ucacue.edu.ec",
    emailPersonal: "",
    sexo: "N/A",
    rolesUsuario: [
      {
        tipoRol: "INVITADO",
        detallesRol: [
          {
            nombreCarrera: "Acceso Público",
            unidadAcademica: "Visitante",
            sede: "Externo",
            modalidad: "Web",
            curso: "",
            paralelo: "",
            nombreRol: "INVITADO",
            unidadOrganizativa: "Visitante",
          },
        ],
      },
    ],
  };
}

function buildSearchUserPayload() {
  const esInvitado = getAuthValue("guest") === "true";
  const esAnonimo = getAuthValue("anonimo") === "true";

  if (esInvitado) return buildGuestUser(getAuthValue("guest_code"));
  if (esAnonimo) return buildAnonUser();

  const userSsoRaw = getAuthValue("user_sso");
  const userSsoRolesRaw = getAuthValue("user_sso_roles");
  const userSso = userSsoRaw ? safeParse(userSsoRaw) : null;
  const userSsoRoles = userSsoRolesRaw ? safeParse(userSsoRolesRaw) : null;
  const userPrincipal =
    Array.isArray(userSsoRoles) && userSsoRoles.length > 0 ? userSsoRoles[0] : userSso;
  const userNormalizado = normalizeSsoUserForUsage(userPrincipal, userSsoRoles);

  return userNormalizado || buildAnonUser();
}

function buildApiUrl(path) {
  if (API_BASE.startsWith("http")) {
    return `${API_BASE.replace(/\/$/, "")}${path}`;
  }
  return `${API_BASE.replace(/\/$/, "")}${path}`;
}

function sendSearchLog(terminoBusqueda, urlBusqueda) {
  const token = getAuthValue("token");
  const esInvitado = getAuthValue("guest") === "true";
  const esAnonimo = getAuthValue("anonimo") === "true";
  const headers = { "Content-Type": "application/json" };

  if (token && !esInvitado && !esAnonimo) {
    headers.Authorization = `Bearer ${token}`;
  }

  const payload = {
    terminoBusqueda,
    urlBusqueda,
    usuario: buildSearchUserPayload(),
  };

  return fetch(buildApiUrl("/log-uso/registrar-busqueda"), {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
    keepalive: true,
  });
}

const Metabuscador = () => {
  const [query, setQuery] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    const normalizedQuery = query.trim();
    if (!normalizedQuery) return;

    localStorage.setItem("ultimaBusqueda", query);

    const base = "https://research-ebsco-com.vpn.ucacue.edu.ec/c/q3t66c/search/results";
    const fullUrl = `${base}?limiters=FT1%253AY&q=${encodeURIComponent(normalizedQuery)}`;

    // Best-effort: nunca bloquear la navegación del usuario por falla de log.
    sendSearchLog(normalizedQuery, fullUrl).catch(() => {});

    window.open(fullUrl, "_blank");
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-[720px]">

      <div className="relative p-[2px] rounded-full animated-border">

        <div className="relative bg-white rounded-full">

          <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-[14px]" />

          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar artículos, libros y más"
            className="
              w-full h-11 md:h-10 rounded-full border border-gray-300 bg-white
              pl-10 pr-4 text-[14px] text-gray-900
              placeholder:text-gray-400
              outline-none
              focus:border-gray-400
              focus:ring-2 focus:ring-black/5
              transition
            "
          />

          <button type="submit" className="sr-only">
            Buscar
          </button>

        </div>

      </div>

    </form>
  );
};

export default Metabuscador;
